import {
  ScreeningRule,
  ScreeningRuleDraft,
  ScreeningRuleProposition,
  ScreeningRuleRow
} from '../../../data/model/screeningRules'
import {
  SourceQuestion,
  clauseToRows,
  describeRow,
  isRowComplete
} from '../audienceConditions'

export type { SourceQuestion } from '../audienceConditions'
export {
  getSourceQuestions,
  toSelectOptions,
  isRowComplete
} from '../audienceConditions'

export const toRows = (rule: ScreeningRule): ScreeningRuleRow[] =>
  clauseToRows(rule.condition)

/**
 * One clause per row, incomplete rows dropped. The AND reads as an AND downstream only because the backend
 * stamps each clause's index — this never sends clause numbers.
 */
export const buildCondition = (
  rows: ScreeningRuleRow[],
  surveyId: string
): ScreeningRuleProposition[][] =>
  rows.filter(isRowComplete).map((row) =>
    row.responseOptionIds.map((responseOptionId) => ({
      proposition: { surveyId, questionId: row.questionId, responseOptionId },
      negated: row.negated
    }))
  )

/**
 * Splits a single-clause rule into the separate rules an old Or rule was always equivalent to. Must run
 * before `toDraft`, so each part owns its own `originalCondition` rather than the parent's. Groups on
 * negation too, since `toRows` takes a group's `negated` from its first proposition.
 */
export const splitLegacyOrRules = (rules: ScreeningRule[]): ScreeningRule[] =>
  rules.flatMap((rule) => {
    if (rule.condition.length !== 1) return rule

    const groups = new Map<string, ScreeningRuleProposition[]>()
    rule.condition[0].forEach((proposition) => {
      const key = `${proposition.proposition.questionId}|${proposition.negated}`
      const group = groups.get(key)
      if (group) {
        group.push(proposition)
        return
      }
      groups.set(key, [proposition])
    })

    if (groups.size < 2) return rule

    return [...groups.values()].map((group) => ({
      __typename: rule.__typename,
      condition: [group]
    }))
  })

export const toDraft = (rule: ScreeningRule): ScreeningRuleDraft => ({
  rows: toRows(rule),
  originalCondition: rule.condition,
  edited: false
})

/**
 * Rules in the order the screener holds them, which is their identity — there is nothing to sort by. The
 * undefined case is the query still loading.
 */
export const rulesInOrder = (
  rules: ScreeningRule[] | undefined | null
): ScreeningRule[] => rules ?? []

export const isDraftComplete = (draft: ScreeningRuleDraft): boolean =>
  draft.rows.length > 0 && draft.rows.every(isRowComplete)

/** `Screen out if A1 is not one of Cruise, Tour AND A2 is one of Never` */
export const describeRule = (
  draft: ScreeningRuleDraft,
  questions: SourceQuestion[]
): string => draft.rows.map((row) => describeRow(row, questions)).join(' AND ')

/** Questions a rule references that screen out on their own first — the warning the dialog shows. */
export const questionsThatScreenOutFirst = (
  draft: ScreeningRuleDraft,
  questions: SourceQuestion[]
): SourceQuestion[] =>
  questions.filter(
    (question) =>
      question.screensOutOnItsOwn &&
      draft.rows.some((row) => row.questionId === question.questionId)
  )

/** Targeting questions a rule references — moving their screen in-survey re-prices the job. */
export const targetingQuestionsReferenced = (
  draft: ScreeningRuleDraft,
  questions: SourceQuestion[]
): SourceQuestion[] =>
  questions.filter(
    (question) =>
      question.isTargeting &&
      draft.rows.some((row) => row.questionId === question.questionId)
  )
